import type { GameState } from './types';

/**
 * **Mur payant** (v0.55.0, A112) : le jeu est gratuit jusqu'au boss du
 * niveau 2 inclus ; le passage au niveau 3 est fermé tant que le jeu n'est pas
 * acheté (« entitled » : achat Google Play, code maison ou debug).
 *
 * Le cœur ne sait rien de Google Play ni des codes : il reçoit un booléen de
 * l'interface (`Game.setEntitled`), jamais sauvegardé. Seul `gateReached` (le
 * boss du dernier niveau gratuit a été battu sans achat) est persisté.
 *
 * Sauvegardes anciennes déjà au-delà du niveau 2 sans achat (préprod,
 * testeurs) : rien de rétroactif à l'ouverture, mais le mur bloque le
 * **prochain** passage de niveau, quel qu'il soit (`level >= FREE_LEVELS`).
 *
 * Module pur : aucune dépendance au reste du cœur hors types.
 */

/** Dernier niveau gratuit (A112) : le joueur bat le boss du niveau 2, le niveau 3 est payant. */
export const FREE_LEVELS = 2;

/** Le niveau `level` se joue-t-il sans achat ? */
export function isFreeLevel(level: number): boolean {
  return level <= FREE_LEVELS;
}

/**
 * L'Invasion gagnée au niveau `level` ouvre-t-elle le mur (au lieu du niveau
 * suivant) ? Vrai pour tout niveau ≥ `FREE_LEVELS` sans achat : au niveau 2
 * (le cas normal) comme au-delà (sauvegarde ancienne non achetée).
 */
export function gateBlocks(level: number, entitled: boolean): boolean {
  return !entitled && level >= FREE_LEVELS;
}

export interface GateStatus {
  freeLevels: number;
  /** Jeu acheté (ou code, ou debug) : rien n'est fermé. */
  entitled: boolean;
  /** Le boss du niveau `freeLevels` a été battu sans achat : le niveau suivant est dû. */
  reached: boolean;
  /** Le joueur est bloqué par le mur : non acheté et au niveau `freeLevels` (ou au-delà par une sauvegarde ancienne). */
  locked: boolean;
}

/** État du mur pour l'interface (écran d'achat, Accalmie, titre). */
export function gateStatus(state: Pick<GameState, 'level' | 'entitled' | 'gateReached'>): GateStatus {
  const entitled = state.entitled === true;
  return {
    freeLevels: FREE_LEVELS,
    entitled,
    reached: state.gateReached === true,
    locked: gateBlocks(state.level, entitled),
  };
}
